"use client";

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useToast } from "../components/ui/use-toast";
import { Check, X } from "lucide-react";
import { RisksCombobox, RisksProvider } from "../components/ui/RisksCombobox";
import type { Risk as ProjectRisk } from "../types/risks";

// Local type for the combobox
type Risk = {
  id: string;
  name: string;
  description?: string;
  category?: string;
  rating?: string;
  originalRisk: ProjectRisk;
};

export function RisksComboboxExample() {
  const [selectedRisks, setSelectedRisks] = React.useState<string[]>([]);
  const [showSelected, setShowSelected] = React.useState(false);
  
  // Example of programmatically setting selected risks
  const setExampleRisks = () => {
    setSelectedRisks(['risk-1', 'risk-3']);
  };
  
  // Clear all selected risks
  const clearAll = () => {
    setSelectedRisks([]);
  };
  
  // Toggle showing selected risks
  const toggleShowSelected = () => {
    setShowSelected(!showSelected);
  };
  
  // Handle when a risk is selected
  const handleRiskSelect = (riskId: string) => {
    console.log(`Risk selected: ${riskId}`);
  };
  
  // Handle when a risk is deselected
  const handleRiskDeselect = (riskId: string) => {
    console.log(`Risk deselected: ${riskId}`);
  };
  
  // Handle when all risks are selected
  const handleAllSelect = () => {
    console.log('All risks selected');  
  };
  
  // Handle when all risks are deselected
  const handleAllDeselect = () => {
    console.log('All risks deselected');
  };
  
  // Example of custom risks data
  const customRisks: Risk[] = [
    { 
      id: 'custom-1', 
      name: 'Custom Risk 1', 
      description: 'This is a custom risk example',
      category: 'Custom',
      rating: 'High',
      originalRisk: {
        id: 'custom-1',
        description: 'This is a custom risk example',
        category: 'Custom',
        riskRating: 'High',
        status: 'Active',
        type: 'Other',
        dateAdded: new Date().toISOString(),
        owner: 'System',
        criticality: 'High',
        risk: 'High',
        dataClassification: 'Business Confidential',
        subprocessor: false,
        publishInPrivacyPolicy: false,
        annualSecurityReviewCompleted: false,
        vendorManagementNeeded: false,
        dataTypesShared: [],
        dataStorageLocations: [],
        portable: false,
        physicalLabel: false,
        socCriticality: 'Not Critical',
        backupsRequired: false,
        treatmentRequired: 'No',
        menus: {}
      } as unknown as ProjectRisk
    },
    { 
      id: 'custom-2', 
      name: 'Custom Risk 2', 
      description: 'Another custom risk example',
      category: 'Custom',
      rating: 'Medium',
      originalRisk: {
        id: 'custom-2',
        description: 'Another custom risk example',
        category: 'Custom',
        riskRating: 'Medium',
        status: 'Active',
        type: 'Other',
        dateAdded: new Date().toISOString(),
        owner: 'System',
        criticality: 'Medium',
        risk: 'Medium',
        dataClassification: 'Business Confidential',
        subprocessor: false,
        publishInPrivacyPolicy: false,
        annualSecurityReviewCompleted: false,
        vendorManagementNeeded: false,
        dataTypesShared: [],
        dataStorageLocations: [],
        portable: false,
        physicalLabel: false,
        socCriticality: 'Not Critical',
        backupsRequired: false,
        treatmentRequired: 'No',
        menus: {}
      } as unknown as ProjectRisk
    }
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Risks Combobox</CardTitle>
          <CardDescription>
            Select one or more risks from the dropdown. The component supports search, filtering by category,
            and displays selected items as badges.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="risks-combobox">Select Risks</Label>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={setExampleRisks}
                >
                  Set Example
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearAll}
                >
                  Clear All
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={toggleShowSelected}
                >
                  {showSelected ? 'Hide Selected' : 'Show Selected'}
                </Button>
              </div>
            </div>
            
            {/* Using the RisksCombobox with default risks from context */}
            <RisksProvider>
              <RisksCombobox
                selected={selectedRisks}
                setSelected={setSelectedRisks}
                placeholder="Search and select risks..."
                onRiskSelect={handleRiskSelect}
                onRiskDeselect={handleRiskDeselect}
                onAllSelect={handleAllSelect}
                onAllDeselect={handleAllDeselect}
                className="w-full"
              />
            </RisksProvider>
            
            {/* Display selected risks */}
            {showSelected && selectedRisks.length > 0 && (
              <div className="mt-4 p-4 border rounded-md bg-muted/20">
                <h4 className="text-sm font-medium mb-2">Selected Risks:</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedRisks.map(riskId => {
                    // In a real app, you'd look up the risk details by ID
                    const risk = [
                      { id: 'risk-1', name: 'Data Breach', category: 'Security', rating: 'High' },
                      { id: 'risk-2', name: 'System Downtime', category: 'Availability', rating: 'Medium' },
                      { id: 'risk-3', name: 'Regulatory Non-compliance', category: 'Compliance', rating: 'High' },
                      { id: 'risk-4', name: 'Vendor Failure', category: 'Vendor', rating: 'Medium' },
                      { id: 'risk-5', name: 'Data Loss', category: 'Security', rating: 'Extreme' },
                      ...customRisks
                    ].find(r => r.id === riskId);
                    
                    if (!risk) return null;
                    
                    return (
                      <div 
                        key={risk.id} 
                        className="flex items-center gap-2 bg-background border rounded-md px-3 py-1 text-sm"
                      >
                        <span className="font-medium">{risk.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {risk.category}
                        </Badge>
                        {risk.rating && (
                          <Badge 
                            variant={risk.rating === 'Extreme' ? 'destructive' : 'default'}
                            className="text-xs"
                          >
                            {risk.rating}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="custom-risks">Custom Risks Example</Label>
            <p className="text-sm text-muted-foreground">
              Example with a custom list of risks passed directly to the component
            </p>
            
            {/* Example with custom risks passed directly */}
            <RisksCombobox
              selected={selectedRisks}
              setSelected={setSelectedRisks}
              risks={customRisks}
              placeholder="Search custom risks..."
              className="w-full"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Example Code</Label>
            <pre className="bg-muted p-4 rounded-md text-sm overflow-x-auto">
              <code>
{`// Basic usage with context provider
<RisksProvider>
  <RisksCombobox
    selected={selectedRisks}
    setSelected={setSelectedRisks}
    placeholder="Search and select risks..."
  />
</RisksProvider>

// With custom risks array
<RisksCombobox
  selected={selectedRisks}
  setSelected={setSelectedRisks}
  risks={customRisks}
  placeholder="Search custom risks..."
  onRiskSelect={(riskId) => console.log('Selected:', riskId)}
  onRiskDeselect={(riskId) => console.log('Deselected:', riskId)}
  onAllSelect={() => console.log('All selected')}
  onAllDeselect={() => console.log('All deselected')}
  className="w-full"
  fullWidth
/>`}
              </code>
            </pre>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Features</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Search and filter risks by name or description</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Filter by category with clickable category chips</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Multi-select with badges for selected items</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Keyboard navigation and accessibility support</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Customizable with props for selected values, callbacks, and styling</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Works with or without the RisksProvider context</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

export default RisksComboboxExample;
